// CANONICAL COPY (GH #7115). src/engine/obligationEvaluator.js re-exports this file so the browser and the
// Function App share one compliance engine — one definition of "compliant". Edit here, never there.
'use strict';

/**
 * src/engine/obligationEvaluator.js
 *
 * Core obligation evaluation engine (Phase 1).
 *
 * Materialises tasks from enabled obligations by evaluating fulfilment
 * criteria against current tenant state.  Reuses checker functions from
 * autoEvidence.js — no duplication.
 *
 * MaterialisedTask shape (computed, never stored):
 *   { obligationId, title, description, controlIds, status,
 *     dueDate, fulfilmentState, domainActions, evidenceRefs,
 *     assigneeId?, snoozedUntil? }
 */

const { evaluateAutoEvidence } = require('./autoEvidence.cjs');
const { withoutSampleRecords } = require('../sampleRecords.cjs');
const { planWithheldAmong, withheldModuleNames } = require('./planGateMarker.cjs');
const { OBLIGATIONS_BY_ID } = require('./obligations/index.cjs');
const { cadenceToMs } = require('./obligations/index.cjs');
const { getEquivalentControls } = require('./controlMappings.cjs');
const { deriveResponsibility } = require('../obligationResponsibility.cjs');
const {
  obligationWorkflowCriterion,
  obligationWorkflowAnnotation,
} = require('../workflows/workflowEvidence.cjs');

// ── PDWB-03 — workflow runs and obligations (design §3e, §3n) ────────────────
//
// `workflow-run-current` is not a catalogue criterion: it is appended at
// evaluation, for an obligation a published workflow links, and judged by the
// one countability rule through workflowEvidence.cjs (a run counts only for an
// obligation its own frozen version linked; D11 = A, a self-reviewed pass
// never counts). It is appended under the obligation's DECLARED strategy and
// never relaxes it: 'all' and 'manual' now additionally require it, 'any'
// accepts it as one more way to be met. It does not stand in for a manual
// obligation's residual attestation — that would relax the human-held fact
// (evaluateManualObligation) and is an open founder decision. No verdict yet
// (every linked workflow inside its first window) or evidence that is not
// `ok` (not in plan, not readable) → not appended: the obligation's own
// catalogue criteria decide, exactly as for a tenant with no workflows.
const WORKFLOW_CRITERION = 'workflow-run-current';

function workflowCriterionResult(def, state) {
  const c = obligationWorkflowCriterion(state, def.id, {
    now: Date.now(),
    enabledObligations: (state && state.enabledObligations) || [],
    obligationsById: OBLIGATIONS_BY_ID,
  });
  if (!c) return null;
  return {
    type: WORKFLOW_CRITERION,
    description: 'A linked workflow has a countable run inside this obligation\'s window',
    pass: c.pass,
    summary: c.summary,
    details: c.details,
  };
}

/** Appends the criterion to an autoEvidence evaluation and re-derives canAutoComplete. */
function withWorkflowCriterion(evaluation, def, state, strategy) {
  const extra = workflowCriterionResult(def, state);
  if (!extra) return evaluation;
  const results = [...evaluation.results, extra];
  const passCount = results.filter((r) => r.pass).length;
  const anyNotEntitled = results.some((r) => r.notEntitled);
  return {
    ...evaluation,
    results,
    canAutoComplete: !anyNotEntitled && (strategy === 'any' ? passCount > 0 : passCount === results.length),
  };
}

/**
 * Annotates a returned obligation task with the workflow doing its work (never
 * drops it — it stays in every count, overdue included; co-owner item 4), and
 * puts the workflow's own action first: open the run whose steps are the work,
 * or start a run of a manual workflow. A scheduled workflow gets no start
 * button (a manual start of it is refused).
 */
function annotateWorkflow(task, state) {
  if (!task) return task;
  const wf = obligationWorkflowAnnotation(state, task.obligationId, { now: Date.now() });
  if (!wf) return task;
  const action =
    wf.state === 'run-open'
      ? { type: 'workflow-run', label: `Open the run of "${wf.title}"`, definitionId: wf.definitionId, runId: wf.runId }
      : wf.state === 'start-run'
        ? { type: 'workflow-start', label: `Start a run of "${wf.title}"`, definitionId: wf.definitionId }
        : null;
  return {
    ...task,
    workflow: wf,
    domainActions: action ? [action, ...(task.domainActions || [])] : task.domainActions,
  };
}

// ── Per-product evidence checkers ────────────────────────────────────────────
// Each checker takes a product record and returns { pass, details }.

const PRODUCT_CHECKERS = {
  'product-sbom-uploaded': (product) => ({
    pass: !!product.sbomRef,
    details: product.sbomRef
      ? `${product.sbomFormat || 'SBOM'} ${product.sbomVersion || ''}`.trim()
      : 'No SBOM uploaded',
  }),

  'product-support-period-valid': (product) => {
    const endDate = product.supportPeriodEnd ? new Date(product.supportPeriodEnd) : null;
    const valid = !!endDate && endDate.getTime() > Date.now();
    return {
      pass: valid,
      details: !endDate
        ? 'No support period end date set'
        : valid
          ? `Support until ${product.supportPeriodEnd}`
          : `Support period expired ${product.supportPeriodEnd}`,
    };
  },

  'product-conformity-refs-set': (product) => {
    const hasCe = !!product.ceMarkRef;
    const hasEuDoc = !!product.euDeclarationOfConformityRef;
    return {
      pass: hasCe && hasEuDoc,
      details:
        !hasCe && !hasEuDoc
          ? 'CE mark ref and EU declaration of conformity ref both missing'
          : !hasCe
            ? 'CE mark ref missing'
            : 'EU declaration of conformity ref missing',
    };
  },

  'product-tech-doc-set': (product) => ({
    pass: !!product.technicalDocumentationRef,
    details: product.technicalDocumentationRef
      ? 'Technical documentation ref set'
      : 'No technical documentation ref set',
  }),

  'product-risk-assessment-current': (product) => {
    const TWELVE_MONTHS_MS = 365 * 24 * 60 * 60 * 1000;
    const hasRef = !!product.riskAssessmentRef;
    const assessDate = product.riskAssessmentDate ? new Date(product.riskAssessmentDate) : null;
    const isCurrent = !!assessDate && Date.now() - assessDate.getTime() < TWELVE_MONTHS_MS;
    return {
      pass: hasRef && isCurrent,
      details: !hasRef
        ? 'No risk assessment ref set'
        : !assessDate
          ? 'Risk assessment date not set'
          : !isCurrent
            ? `Risk assessment dated ${product.riskAssessmentDate} is older than 12 months`
            : `Risk assessment current (dated ${product.riskAssessmentDate})`,
    };
  },
};

// ── Per-product evaluation ────────────────────────────────────────────────────

function evaluatePerProductObligation(def, enabledObligation, state, options = {}) {
  const products = (state.products || []).filter(
    (p) => !p.deletedAt && p.craRole !== 'not-in-scope'
  );
  const controlIds = resolveControlIds(def);
  const dueDate = computeDueDate(enabledObligation, def);
  const criteria = def.perProductCriteria || [];

  // #6608 — the same trap as every empty-population branch in autoEvidence,
  // in this file: an emptied product register reads as "no in-scope products"
  // and returns 'not-applicable', which claims the obligation does not apply
  // rather than that the register was withheld. `products` is gated by no plan
  // today; the check is here so it cannot become one silently.
  const withheldProducts = planWithheldAmong(state, ['products']);
  if (withheldProducts.length > 0) {
    return {
      obligationId: def.id,
      title: def.title,
      description: def.description,
      controlIds,
      status: NOT_ENTITLED_STATUS,
      notEntitled: true,
      notEntitledModules: withheldModuleNames(withheldProducts),
      dueDate: null,
      fulfilmentState: [],
      domainActions: def.domainActions || [],
      evidenceRefs: [],
      assigneeId: enabledObligation.assigneeId || null,
      snoozedUntil: null,
      perProductProgress: { total: 0, met: 0, products: [] },
    };
  }

  // No in-scope products → N/A (not met, not unmet)
  if (products.length === 0) {
    return {
      obligationId: def.id,
      title: def.title,
      description: def.description,
      controlIds,
      status: 'not-applicable',
      dueDate,
      fulfilmentState: [],
      domainActions: def.domainActions || [],
      evidenceRefs: [],
      assigneeId: enabledObligation.assigneeId || null,
      snoozedUntil: null,
      perProductProgress: { total: 0, met: 0, products: [] },
    };
  }

  const productResults = products.map((product) => {
    const criteriaResults = criteria.map((criterion) => {
      const checker = PRODUCT_CHECKERS[criterion.type];
      if (!checker) {
        return {
          criterionType: criterion.type,
          pass: false,
          details: `Unknown criterion: ${criterion.type}`,
        };
      }
      const result = checker(product);
      return { criterionType: criterion.type, ...result };
    });
    return {
      id: product.id,
      name: product.name || product.id,
      pass: criteriaResults.every((r) => r.pass),
      criteriaResults,
    };
  });

  const metCount = productResults.filter((r) => r.pass).length;
  const total = products.length;

  // All products satisfied → fulfilled.
  //
  // GH #6698 — here the synthesised all-pass display was always HONEST: this
  // strategy fulfils only when every product passes every criterion, so there
  // is no criterion whose verdict is being papered over. The `any` strategy
  // below is the one that was lying.
  if (metCount === total) {
    if (!options.includeFulfilled) return null;
    return {
      obligationId: def.id,
      title: def.title,
      description: def.description,
      controlIds,
      status: 'fulfilled',
      dueDate,
      fulfilmentState: [
        {
          type: 'per-product',
          pass: true,
          summary: `All ${total} product${total !== 1 ? 's have' : ' has'} the required evidence`,
        },
      ],
      machineAsserted: [],
      residualConfirmation: null,
      domainActions: def.domainActions || [],
      evidenceRefs: [],
      assigneeId: enabledObligation.assigneeId || null,
      snoozedUntil: null,
      perProductProgress: { total, met: metCount, products: productResults },
    };
  }

  const now = Date.now();
  const isPastDue = dueDate && new Date(dueDate).getTime() <= now;
  const status = isPastDue ? 'overdue' : 'unmet';

  return {
    obligationId: def.id,
    title: def.title,
    description: def.description,
    controlIds,
    status,
    dueDate,
    fulfilmentState: [
      {
        type: 'per-product',
        pass: false,
        summary: `${metCount} of ${total} product${total !== 1 ? 's' : ''} have the required evidence`,
      },
    ],
    domainActions: def.domainActions || [],
    evidenceRefs: [],
    assigneeId: enabledObligation.assigneeId || null,
    snoozedUntil: null,
    perProductProgress: { total, met: metCount, products: productResults },
  };
}

// ── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Resolve all control IDs for an obligation via its scfIds.
 * Expands each TCF ID to include all equivalent framework controls.
 */
function resolveControlIds(obligationDef) {
  const expanded = new Set();
  for (const scfId of obligationDef.scfIds) {
    for (const equiv of getEquivalentControls(scfId)) {
      expanded.add(equiv);
    }
  }
  return [...expanded];
}

/**
 * Compute due date from enabledAt + cadence.
 * For domain-anchored cadences, uses the enabledAt timestamp.
 */
function computeDueDate(enabledObligation, obligationDef) {
  const freq = enabledObligation.cadenceOverride?.frequency || obligationDef.cadence.frequency;
  const ms = cadenceToMs(freq);
  if (!ms) return null; // 'once' — no recurring due date
  const base = new Date(enabledObligation.enabledAt || Date.now());
  return new Date(base.getTime() + ms).toISOString();
}

/**
 * Bridge an ObligationDefinition into the shape that evaluateAutoEvidence
 * expects (a standardTask-like object with autoEvidence).
 */
function toAutoEvidenceFormat(obligationDef) {
  if (obligationDef.strategy === 'manual' || obligationDef.fulfilmentCriteria.length === 0) {
    return null;
  }
  return {
    scfIds: obligationDef.scfIds,
    scfId: obligationDef.scfIds[0],
    obligationId: obligationDef.id,
    autoEvidence: {
      strategy: obligationDef.strategy,
      conditions: obligationDef.fulfilmentCriteria,
    },
  };
}

/**
 * Split a fulfilmentState array into what the machine can prove
 * (machineAsserted) and the single off-system fact a human must still
 * confirm (residualConfirmation) — the 'manual-attestation' criterion.
 */
function splitResidual(fulfilmentState, machineAssertedPassSummaries) {
  const machineAsserted = fulfilmentState.filter((f) => f.type !== 'manual-attestation');
  const residual = fulfilmentState.find((f) => f.type === 'manual-attestation') || null;
  const residualConfirmation = residual
    ? { ...residual, machineContext: machineAssertedPassSummaries }
    : null;
  return { machineAsserted, residualConfirmation };
}

/**
 * The task shape for an obligation that IS fulfilled, built from what its
 * criteria actually returned.
 *
 * GH #6698. Shares `splitResidual`'s contract with `evaluateManualObligation`,
 * including passing only the PASSING machine-asserted summaries as context —
 * a criterion that did not pass is not "already known".
 */
function buildFulfilledTask(def, enabledObligation, controlIds, dueDate, results) {
  const fulfilmentState = results.map((r) => ({
    type: r.type,
    pass: r.pass,
    summary: r.summary,
    waived: r.waived || false,
    notEntitled: r.notEntitled || false,
  }));
  const { machineAsserted, residualConfirmation } = splitResidual(
    fulfilmentState,
    fulfilmentState.filter((f) => f.type !== 'manual-attestation' && f.pass).map((f) => f.summary)
  );
  return {
    obligationId: def.id,
    title: def.title,
    description: def.description,
    controlIds,
    status: 'fulfilled',
    dueDate,
    fulfilmentState,
    machineAsserted,
    residualConfirmation,
    domainActions: def.domainActions || [],
    evidenceRefs: results
      .filter((r) => r.pass && !r.waived && r.details)
      .map((r) => ({ type: r.type, details: r.details })),
    assigneeId: enabledObligation.assigneeId || null,
    snoozedUntil: null,
  };
}

// ── The not-entitled obligation status (GH #6608, #6627) ────────────────────
//
// autoEvidence returns `pass: null, notEntitled: true` for a criterion whose
// inputs PLAN-B-001's read-side filter withheld from this response — see the
// long note above CHECKER_STATE_DEPENDENCIES there. This layer's job is the
// second half of the founder's decision on #6608: **no obligation strategy may
// ever count such a criterion as fulfilment.**
//
// That is stronger than "a null does not pass". strategy:'any' fulfils an
// obligation on ONE passing criterion, and evaluateAllObligations() then
// re-renders every OTHER criterion of a fulfilled obligation as 'Fulfilled' —
// including one that was never assessed. So a single withheld criterion makes
// the whole obligation NOT-ENTITLED, whatever the strategy and whatever the
// other criteria say. TRYGGR_INTENT.md §5: the system may under-claim, never
// over-claim.
//
// The status is its own value, not 'unmet' and not 'not-applicable':
//
//   'unmet'           asserts the tenant has failed to do something. They have
//                     not — they were not sold the module, and putting
//                     remediation in front of them is #6385.
//   'not-applicable'  asserts the obligation does not apply to this tenant.
//                     It does apply; the product just cannot see the evidence.
//
// `dueDate` is nulled deliberately. A due date is a demand, and there is
// nothing here for the tenant to be late for.
const NOT_ENTITLED_STATUS = 'not-entitled';

function notEntitledModulesOf(results) {
  const seen = [];
  for (const r of results) {
    if (!r.notEntitled) continue;
    for (const m of r.details?.planWithheldModules || []) if (!seen.includes(m)) seen.push(m);
  }
  return seen;
}

/**
 * A waived criterion is an explicit, recorded, justified human decision — the
 * "AI proposes, human confirms" pattern this codebase applies everywhere a
 * machine verdict could be wrong. A tenant who has justified a criterion away
 * has answered it, so the waiver clears the not-entitled flag along with the
 * verdict; without this, an obligation on a module the plan excludes could
 * never be closed by any means at all.
 */
function applyWaivers(results, enabledObligation) {
  const waivers = enabledObligation.criteriaWaivers || [];
  if (waivers.length === 0) return results;
  const waiverMap = new Map(waivers.map((w) => [w.criterionType, w]));
  return results.map((r) => {
    const waiver = waiverMap.get(r.type);
    if (!waiver) return r;
    return {
      ...r,
      pass: true,
      waived: true,
      notEntitled: false,
      summary: `Waived: ${waiver.justification}`,
    };
  });
}

/**
 * Evaluate a manual-strategy obligation.
 *
 * Manual obligations carry fulfilmentCriteria same as any other obligation,
 * but always require ALL of them — including the residual 'manual-attestation'
 * criterion where present — regardless of the catalog's strategy label. A
 * published/current document is machine-checkable evidence that a policy or
 * procedure exists; it is never treated as proof the underlying activity
 * (a pentest, a backup test, a review) actually occurred. That single fact
 * stays with the human, surfaced as a distinct residual confirmation rather
 * than folded into a blanket 'unmet'.
 */
function evaluateManualObligation(def, enabledObligation, state, controlIds, dueDate, now) {
  const base = {
    obligationId: def.id,
    title: def.title,
    description: def.description,
    controlIds,
    dueDate,
    domainActions: def.domainActions,
    assigneeId: enabledObligation.assigneeId || null,
    snoozedUntil: null,
  };

  const isPastDue = dueDate && new Date(dueDate).getTime() <= now;

  if (def.fulfilmentCriteria.length === 0) {
    return {
      ...base,
      status: isPastDue ? 'overdue' : 'unmet',
      fulfilmentState: [],
      machineAsserted: [],
      residualConfirmation: null,
      evidenceRefs: [],
    };
  }

  const bridged = {
    scfIds: def.scfIds,
    scfId: def.scfIds[0],
    obligationId: def.id,
    autoEvidence: { strategy: 'all', conditions: def.fulfilmentCriteria },
  };
  const evaluation = withWorkflowCriterion(evaluateAutoEvidence(bridged, state), def, state, 'all');

  const results = applyWaivers(evaluation.results, enabledObligation);

  const allPass = results.every((r) => r.pass);
  if (allPass) return null; // fulfilled — machine evidence AND human attestation both current

  const fulfilmentState = results.map((r) => ({
    type: r.type,
    pass: r.pass,
    summary: r.summary,
    waived: r.waived || false,
    notEntitled: r.notEntitled || false,
  }));

  const machineAssertedPassSummaries = fulfilmentState
    .filter((f) => f.type !== 'manual-attestation' && f.pass)
    .map((f) => f.summary);
  const { machineAsserted, residualConfirmation } = splitResidual(
    fulfilmentState,
    machineAssertedPassSummaries
  );

  const evidenceRefs = results
    .filter((r) => r.pass && !r.waived && r.details)
    .map((r) => ({ type: r.type, details: r.details }));

  // #6608 — a withheld criterion outranks every other status this obligation
  // could carry, including 'overdue': the tenant cannot be late for work their
  // plan does not include.
  const notEntitledModules = notEntitledModulesOf(results);
  if (notEntitledModules.length > 0) {
    return {
      ...base,
      dueDate: null,
      status: NOT_ENTITLED_STATUS,
      notEntitled: true,
      notEntitledModules,
      fulfilmentState,
      machineAsserted,
      residualConfirmation,
      evidenceRefs,
    };
  }

  const anyPass = results.some((r) => r.pass);
  const status = isPastDue ? 'overdue' : anyPass ? 'partially-satisfied' : 'unmet';

  return {
    ...base,
    status,
    fulfilmentState,
    machineAsserted,
    residualConfirmation,
    evidenceRefs,
  };
}

// ── Main evaluation functions ───────────────────────────────────────────────

/**
 * Evaluate a single enabled obligation against tenant state.
 *
 * @param {object} enabledObligation - Tenant's enabled obligation record
 * @param {object} state - Full tenant state
 * @param {{ includeFulfilled?: boolean }} [options] - when `includeFulfilled`
 *   is set, a fulfilled obligation is returned with `status: 'fulfilled'` and
 *   its REAL per-criterion results instead of `null`. See GH #6698.
 * @returns {object|null} MaterialisedTask, or null if fulfilled
 */
/**
 * Evaluate one enabled obligation into a materialised task.
 *
 * Every returned task also carries WHO OWES IT, derived rather than stored: an
 * obligation with no explicit assignee inherits the owner of the control(s) it
 * is mapped to, and one mapped to several controls is owed by every one of
 * their owners (COPA-04). See utils/obligationResponsibility.js.
 *
 * The derivation needs the tenant's user list, which does not live in `state`,
 * so callers that want it pass `options.users`. Without it the task carries
 * `responsibleEmployeeIds: null` — "not derived here", which is deliberately
 * NOT the same as `[]` ("nobody owes it"). Rendering null as either owned or
 * unowned is inventing an answer (COP-B-004).
 */
function evaluateObligation(enabledObligation, rawState, options = {}) {
  // Sample people never count toward an obligation or its owner (GH #7870).
  const state = withoutSampleRecords(rawState);
  const task = annotateWorkflow(evaluateObligationCore(enabledObligation, state, options), state);
  if (!task) return null;
  const responsibility = deriveResponsibility({
    enabledObligation,
    controlIds: task.controlIds || [],
    state,
    users: options.users,
  });
  return { ...task, ...responsibility };
}

function evaluateObligationCore(enabledObligation, state, options = {}) {
  const def = OBLIGATIONS_BY_ID.get(enabledObligation.obligationId);
  if (!def) return null;
  if (enabledObligation.enabled === false) return null;

  const controlIds = resolveControlIds(def);
  const dueDate = computeDueDate(enabledObligation, def);
  const now = Date.now();

  // Per-product obligations evaluate across state.products
  if (def.strategy === 'per-product') {
    return evaluatePerProductObligation(def, enabledObligation, state, options);
  }

  // Manual obligations evaluate their machine-checkable criteria for real —
  // see evaluateManualObligation for why they never auto-satisfy from a
  // document alone.
  if (def.strategy === 'manual') {
    return evaluateManualObligation(def, enabledObligation, state, controlIds, dueDate, now);
  }

  if (def.fulfilmentCriteria.length === 0) {
    return {
      obligationId: def.id,
      title: def.title,
      description: def.description,
      controlIds,
      status: 'unmet',
      dueDate,
      fulfilmentState: [],
      domainActions: def.domainActions,
      evidenceRefs: [],
      assigneeId: enabledObligation.assigneeId || null,
      snoozedUntil: null,
    };
  }

  // Evaluate via existing autoEvidence checkers
  const bridged = toAutoEvidenceFormat(def);
  const evaluation = withWorkflowCriterion(evaluateAutoEvidence(bridged, state), def, state, def.strategy);

  // Apply criteria waivers — override waived criteria with pass
  const results = applyWaivers(evaluation.results, enabledObligation);

  // #6608 — checked BEFORE the fulfilment shortcut below, which is the whole
  // point: `canAutoComplete` for strategy 'any' is `passCount > 0`, so without
  // this an obligation with one real pass and one withheld criterion would
  // return null (fulfilled) and evaluateAllObligations() would then render the
  // withheld criterion as 'Fulfilled' too.
  const notEntitledModules = notEntitledModulesOf(results);

  // All criteria met (including waivers) → fulfilled → no task
  const allPass = results.every((r) => r.pass);
  if (notEntitledModules.length === 0 && (evaluation.canAutoComplete || allPass)) {
    if (!options.includeFulfilled) return null;
    // GH #6698 — THE REAL RESULTS, not a synthesised all-pass.
    //
    // `canAutoComplete` for strategy 'any' is `passCount > 0`, so ONE passing
    // criterion fulfils the obligation. evaluateAllObligations used to rebuild
    // the display from the catalogue definition alone — every criterion
    // `pass: true, summary: 'Fulfilled'` — which meant a criterion that had
    // just returned a real failure, or the honest `pass: null` a checker emits
    // when nobody has recorded the answer, was shown to the customer as a
    // green tick.
    //
    // Concretely: a tenant with forty unclassified vendors and a published
    // sub-processor procedure was told "Sub-processor registry in the Vendors
    // module is complete — Fulfilled". GDPR Art. 28(2) reported met from a
    // document describing the obligation rather than a record discharging it.
    //
    // #6608 established this shape for `notEntitled` and blocked fulfilment
    // outright for it, because a withheld criterion is one the tenant cannot
    // act on. A criterion that merely failed or was never recorded is
    // different — the obligation IS fulfilled under its declared strategy —
    // so the fix here is not to block fulfilment but to stop laundering the
    // other criteria's verdicts through it. What the obligation claims is
    // unchanged; what its criteria claim is now true.
    return buildFulfilledTask(def, enabledObligation, controlIds, dueDate, results);
  }

  // Build fulfilment state from evaluation results
  const fulfilmentState = results.map((r) => ({
    type: r.type,
    pass: r.pass,
    summary: r.summary,
    waived: r.waived || false,
    notEntitled: r.notEntitled || false,
  }));

  // Collect evidence refs from passing criteria details (exclude waived — no real evidence)
  const evidenceRefs = results
    .filter((r) => r.pass && !r.waived && r.details)
    .map((r) => ({
      type: r.type,
      details: r.details,
    }));

  // Determine status: overdue if past due date, otherwise unmet
  const isPastDue = dueDate && new Date(dueDate).getTime() <= now;
  const status =
    notEntitledModules.length > 0 ? NOT_ENTITLED_STATUS : isPastDue ? 'overdue' : 'unmet';

  return {
    obligationId: def.id,
    title: def.title,
    description: def.description,
    controlIds,
    status,
    // See NOT_ENTITLED_STATUS: no due date for work the plan does not include.
    dueDate: notEntitledModules.length > 0 ? null : dueDate,
    fulfilmentState,
    domainActions: def.domainActions,
    evidenceRefs,
    assigneeId: enabledObligation.assigneeId || null,
    snoozedUntil: null,
    ...(notEntitledModules.length > 0 ? { notEntitled: true, notEntitledModules } : {}),
  };
}

/**
 * Evaluate all enabled obligations and return materialised tasks.
 * Fulfilled obligations are filtered out (return null).
 *
 * @param {Array} enabledObligations - Tenant's enabled obligation records
 * @param {object} state - Full tenant state
 * @returns {Array} MaterialisedTask[]
 */
function evaluateObligations(enabledObligations, rawState, options = {}) {
  const state = withoutSampleRecords(rawState);
  return (enabledObligations || [])
    .map((eo) => evaluateObligation(eo, state, options))
    .filter(Boolean);
}

/**
 * Evaluate all enabled obligations INCLUDING fulfilled ones.
 * Fulfilled obligations are returned with status 'fulfilled' so that
 * Gate 5 of the control engine can see that obligations ARE linked
 * to a control even when all criteria are met.
 *
 * @param {Array} enabledObligations - Tenant's enabled obligation records
 * @param {object} state - Full tenant state
 * @returns {Array} MaterialisedTask[] (includes status: 'fulfilled' entries)
 */
function evaluateAllObligations(enabledObligations, rawState, options = {}) {
  const state = withoutSampleRecords(rawState);
  // GH #6698 — this used to rebuild a fulfilled obligation's display from the
  // catalogue definition, marking EVERY criterion `pass: true, summary:
  // 'Fulfilled'`. `evaluateObligation` now returns the real thing.
  return (enabledObligations || [])
    .map((eo) => evaluateObligation(eo, state, { ...options, includeFulfilled: true }))
    .filter(Boolean);
}

/**
 * For Gate 5: check if any obligation linked to a control is overdue.
 *
 * @param {string} controlId - TCF or framework control ID
 * @param {Array} enabledObligations - Tenant's enabled obligation records
 * @param {object} state - Full tenant state
 * @returns {{ current: boolean, overdueObligations: Array }}
 */
function getObligationStatusForControl(controlId, enabledObligations, state) {
  const tasks = evaluateObligations(enabledObligations, state);
  const overdueObligations = tasks.filter(
    (t) => t.status === 'overdue' && t.controlIds.includes(controlId)
  );
  return {
    current: overdueObligations.length === 0,
    overdueObligations,
  };
}

module.exports = {
  evaluateObligation,
  evaluateObligations,
  evaluateAllObligations,
  getObligationStatusForControl,
};
